import React, { useEffect } from "react";
import { Phone, Mail, Hammer, TreePine, Sparkles, Truck, ShieldCheck, Package } from "lucide-react";
import SubpageHero from "@/components/SubpageHero";
import SubpageLayout from "@/components/SubpageLayout";
import SectionNumber from "@/components/SectionNumber";
import Embers from "@/components/Embers";
import BuyButton from "@/components/BuyButton";
import { SITE, BOARDS, formatHUF } from "@/lib/data";
import useScrollReveal from "@/hooks/useScrollReveal";

const VALUES = [
  {
    Icon: Hammer,
    title: "Kézzel készült",
    desc: "Minden deszka kézzel csiszolt, olajozott, egyedi gravírozással.",
  },
  {
    Icon: TreePine,
    title: "Helyi fa",
    desc: "Tölgy, kőris, dió — a hazai erdők lassan szárított anyaga.",
  },
  {
    Icon: Sparkles,
    title: "Epoxi erezet",
    desc: "Folyékony üveg a fában: tűzpiros, kék, arany — a természet és a design találkozása.",
  },
];

export default function Vagodeszkak() {
  useScrollReveal();
  useEffect(() => {
    document.title = "Egyedi vágódeszkák · Pakkaland";
  }, []);

  return (
    <SubpageLayout testId="page-vagodeszkak">
      <SubpageHero
        crumb="Egyedi vágódeszkák"
        eyebrow="Kézműves műhely"
        title="Egyedi vágódeszkák"
        tagline="Kézzel készült vágó- és tálalódeszkák — minden darab egy történet."
        image="/images/jujl9t14.jpg"
      />

      {/* Intro */}
      <section className="relative bg-white py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="01" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 relative">
          <div className="reveal">
            <span className="eyebrow">Műhely</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Egyedi darabok,<br />
              <span className="italic text-pkred">saját történettel.</span>
            </h2>
            <p className="mt-8 text-pkcharcoal/75 text-base sm:text-lg leading-relaxed max-w-3xl">
              A Pakkaland nem csak étel — kézművesség is. Műhelyünkben
              tölgyből, kőrisből és dióból készítünk vágó- és
              tálalódeszkákat. Minden darab kézzel csiszolt, olajozott, és
              epoxi erezettel kiegészített — a természet és a design
              találkozása egyetlen tárgyon.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative bg-pkbeige py-20 sm:py-24 overflow-hidden">
        <SectionNumber n="02" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-6 relative">
          {VALUES.map(({ Icon, title, desc }, i) => (
            <div
              key={title}
              data-testid={`value-${i}`}
              className="bg-white p-8 sm:p-10 card-fire reveal"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-12 h-12 rounded-full bg-pkbeige flex items-center justify-center mb-6">
                <Icon className="w-5 h-5 text-pkred" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-2xl text-pkcharcoal">{title}</h3>
              <p className="text-pkcharcoal/65 text-sm mt-3 leading-relaxed">
                {desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="relative bg-white py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="03" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <div className="max-w-2xl mb-14 reveal">
            <span className="eyebrow">Aktuális darabok</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Limitált<br />
              <span className="italic text-pkred">kollekció.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BOARDS.map((b, i) => (
              <article
                key={b.id || b.title + i}
                data-testid={`product-${i}`}
                className="group reveal flex flex-col"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-pkbeige">
                  <img
                    src={b.img}
                    alt={b.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                  />
                  <div className="absolute top-4 left-4 bg-white px-3 py-1.5 text-[0.65rem] tracking-[0.2em] uppercase font-semibold text-pkcharcoal">
                    {b.sub}
                  </div>
                  {b.price && (
                    <div className="absolute bottom-4 right-4 bg-pkred text-white font-serif text-xl px-4 py-2 shadow-lg">
                      {formatHUF(b.price)}
                    </div>
                  )}
                </div>
                <div className="pt-6 flex-1 flex flex-col">
                  <h3 className="font-serif text-2xl text-pkcharcoal">
                    {b.title}
                  </h3>
                  <p className="text-pkcharcoal/65 text-sm mt-3 leading-relaxed flex-1">
                    {b.desc}
                  </p>
                  {b.id && (
                    <div className="mt-5">
                      <BuyButton product={b} testId={`buy-${b.id}`} />
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="relative bg-pkbeige py-14 sm:py-16 overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid sm:grid-cols-3 gap-px bg-pkash">
          {[
            { Icon: Package, t: "Személyes átvétel", d: "Vezsenyen, foglalás után." },
            { Icon: Truck, t: "Opcionális szállítás", d: "Magyarország területén — egyeztetés szerint." },
            { Icon: ShieldCheck, t: "Stripe biztonság", d: "Bankkártya, SSL-titkosítva." },
          ].map(({ Icon, t, d }, i) => (
            <div key={t} className="bg-pkbeige p-6 sm:p-7 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-pkred" strokeWidth={1.6} />
              </div>
              <div>
                <div className="font-serif text-lg text-pkcharcoal leading-tight">
                  {t}
                </div>
                <div className="text-pkcharcoal/60 text-sm mt-1">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Custom order CTA */}
      <section className="relative bg-pkcharcoal py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="04" variant="dark" />
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            background:
              "radial-gradient(ellipse at 50% 100%, rgba(217,4,41,0.4) 0%, transparent 60%)",
          }}
        />
        <Embers count={14} />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <span className="eyebrow center">Egyedi rendelés</span>
          <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
            Saját ötlet<span className="text-pkred">,</span>
            <br />
            <span className="italic text-pkred">saját deszka.</span>
          </h2>
          <p className="mt-8 text-white/70 text-base sm:text-lg leading-relaxed">
            Méret, fafajta, epoxi szín, gravírozás — mondd el, mire gondoltál,
            és elkészítjük a sajátodat. Tökéletes ajándék is egy különleges
            alkalomra.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={SITE.emailHref} className="btn-fire">
              <Mail className="w-4 h-4" strokeWidth={1.8} />
              Egyedi rendelés
            </a>
            <a href={SITE.phoneHref} className="btn-ghost">
              <Phone className="w-4 h-4" strokeWidth={1.8} />
              Telefonon kérdezek
            </a>
          </div>
        </div>
      </section>
    </SubpageLayout>
  );
}
